import { BlockType, PermissionRole, PermissionType, TextType } from '@next-space/fe-api-idl';
import { useCallback } from 'react';
import { covertBlockToTitle } from 'src/editor/component/menu-list/menu-item';
import { useConfirmDisableSortersUI } from 'src/editor/editor/uikit/use-insert-record-ui';
import { ActivityIds } from 'src/hooks/activities/activity-ids';
import { ActivitiesListType } from 'src/hooks/activities/use-activity';
import { useUpdateTask } from 'src/hooks/activities/use-update-task';
import { useCopyBlockRecord } from 'src/hooks/block/use-copy-block-record';
import { sortRecord } from 'src/hooks/block/use-sort-record';
import { useUpdateSpaceView } from 'src/hooks/space/use-update-space-view';
import { useTransaction } from 'src/hooks/use-transaction';
import { getMindMapEngine } from 'src/mind-map/utils/mind-engine-utils';
import { addColumn } from 'src/redux/managers/block/add-column';
import { moveBlock } from 'src/redux/managers/block/move';
import { updateBlock } from 'src/redux/managers/block/update';
import * as FavoriteManager from 'src/redux/managers/favorite';
import { patchExpand } from 'src/redux/managers/ui/use-fold';
import { uiActions } from 'src/redux/reducers/ui';
import { cache, dispatch } from 'src/redux/store';
import type { SelectBlock } from 'src/redux/types';
import { getDropInfo, reSetDropInfo } from 'src/services/app/hook/use-drop-info';
import { $currentUserCache } from 'src/services/user/current-user';
import { bizTracker } from 'src/utils/biz-tracker';
import { isCollection } from 'src/utils/block-type-utils';
import { filterDescendantId, getRefBlock, getViewId } from 'src/utils/block-utils';
import { moveToEmpty } from 'src/utils/move-to-empty';
import { idsToSelectedBlocks, selectedBlocksToIds } from 'src/utils/select-block-util';
import { TocType } from 'src/views/main/aside/toc/types';
import { useSelectedBlockHistory } from '../use-selected-block-history';
import { ID_ASIDE_SHARE, TOC_PRIVATE } from './helper';
import { Aside } from './types';
import { getOwnerPage } from 'src/hooks/block/use-get-owner-page';
import { moveToCalendar } from 'src/utils/move-to-calendar';

export const useOnDropBlock = () => {
  const updateTask = useUpdateTask();
  const transaction = useTransaction();
  const updateSpaceView = useUpdateSpaceView();
  const { selectedBlockHistory } = useSelectedBlockHistory();
  const confirmDisableSortersUI = useConfirmDisableSortersUI();
  // const copyBlock = useCopyBlock()
  const copyBlock = useCopyBlockRecord();

  const onDrop = async (copy: boolean) => {
    // 如果是alt+拖拽就复制到对应的位置上（参照notion）
    const dropInfo = getDropInfo();
    // 如果选中的块包含子块，需要过滤掉（页面一般不会出现这种情况 ，但导图是可以选中子块的）
    const selectedBlocks = filterDescendantId(cache.ui.selectedBlocks);
    if (!dropInfo) return;

    const {
      position,
      groupValue,
      subGroupValue,
      dropInChild,
      tocInfo,
      cursorArea,
      syncId,
      mindPageId,
      empty,
      sourceRelationParentId,
      targetRelationParentId,
      addSubitem,
      calendarDate,
    } = dropInfo;
    let { viewId, dropId } = dropInfo;
    if (calendarDate) {
      // 向日历中进行拖拽
      transaction(() => {
        moveToCalendar();
      });
      reSetDropInfo();
      return;
    }

    // 多维表子任务，拖拽到[创建子记录]按钮处的逻辑
    if (dropId && addSubitem && viewId) {
      reSetDropInfo();
      const selectIds = selectedBlocks.map((v) => v.blockId);
      const collectionId = getOwnerPage(dropId) ?? cache.blocks[dropId]?.parentId;
      transaction(() => {
        moveBlock(
          selectIds,
          {
            parentId: collectionId!,
            last: true,
            targetRelationParentId: dropId,
          },
          viewId
        );
      });
      return;
    }

    if (empty) {
      transaction(() => {
        moveToEmpty();
      });
      reSetDropInfo();

      const { groupValue, subGroupValue } = empty;
      const newSelectedBlocks = selectedBlocks.map((item) => {
        return {
          ...item,
          groupValues: groupValue ? { [groupValue]: subGroupValue ? [subGroupValue] : [] } : {},
        };
      });
      dispatch(uiActions.update({ selectedBlocks: newSelectedBlocks, selectedCells: [] }));
      return;
    }
    reSetDropInfo();

    if (!position || !dropId) return;

    const dragToc = tocInfo?.dragToc;
    if (!dragToc && !selectedBlocks.length) return;
    const { blocks } = cache;
    let dropBlock = blocks[dropId];
    // 分享 2 级页面会没找不到 dropParentBlock
    const dropParentBlock = blocks[dropBlock?.parentId ?? ''];
    // 如果是影子同步块的话，需要把同步块作为dropBlock，不能把块移动到影子块的subNodes下
    if (dropBlock?.type === BlockType.SYNC_REFERENCE && dropBlock.data.ref?.uuid) {
      dropId = dropBlock.data.ref.uuid;
      dropBlock = blocks[dropId];
    }

    // mindMap
    if (mindPageId) {
      const engine = getMindMapEngine(mindPageId);
      if (position === 'middle' || (position === 'bottom' && dropInChild)) {
        // 移动到收起的节点上时需要展开
        engine?.updateMindNode({
          id: dropId,
          expanded: true,
        });
        patchExpand([dropId], true);
      }
    }

    const ids = dragToc ? [dragToc] : selectedBlocksToIds(selectedBlocks);

    if (position === 'middle' && dropBlock && isCollection(dropBlock.type)) {
      // 拖拽到 collection view page
      viewId = getViewId(dropBlock); // it's ok
    }

    if (viewId && !dragToc) {
      const view = cache.collectionViews[viewId];
      const sortersEnabled = (view?.format.sorters ?? []).filter((it) => !it.disable).length > 0;
      if (sortersEnabled) {
        bizTracker.event('bitable_cancel_sort_dialog', { from_scene: 'manual_sort' });
        await confirmDisableSortersUI(viewId);
      }
    }

    const recordSort =
      dropParentBlock &&
      isCollection(dropParentBlock.type) &&
      ids.every((uuid) => blocks[uuid]?.parentId === dropParentBlock.uuid);

    ids[0] && bizTracker.event('page_moveblock', { block_name: covertBlockToTitle(ids[0]) });

    const collectionGroupInfo = {
      groupValue,
      subGroupValue,
      targetRelationParentId,
      sourceRelationParentId,
    };
    void transaction(async () => {
      if (!dropId) return;
      if (dropId === TOC_PRIVATE) {
        ids.forEach((id) => {
          const _block = blocks[id];
          if (_block) {
            moveBlock([id], { parentId: _block.spaceId, last: true, targetRelationParentId });
            updateBlock(id, {
              parentId: _block.spaceId,
              permissions: [
                {
                  type: PermissionType.USER,
                  role: PermissionRole.EDITOR,
                  userId: $currentUserCache.uuid,
                },
              ],
            });
          }
        });
        return;
      }

      if (!dropBlock) return;

      if (
        tocInfo &&
        tocInfo.dragTocType === TocType.FAVORITE &&
        tocInfo.dragTocDeep === 0 &&
        tocInfo.dragToc
      ) {
        if (position === 'top') {
          FavoriteManager.move(tocInfo.dragToc, {
            parentId: $currentUserCache.currentSpaceViewId,
            before: dropId,
          });
        }

        if (position === 'bottom') {
          FavoriteManager.move(tocInfo.dragToc, {
            parentId: $currentUserCache.currentSpaceViewId,
            after: dropId,
          });
        }
      } else if (
        tocInfo &&
        tocInfo.dragTocType === TocType.SHARE &&
        tocInfo.dragTocDeep === 0 &&
        tocInfo.dragToc
      ) {
        const shareTocs = document.querySelectorAll(`#${ID_ASIDE_SHARE} [data-block-id]`);
        const shareTocIds = Array.from(shareTocs)
          .map((dom) => (dom as HTMLElement).dataset.blockId)
          .filter(Boolean) as string[];

        if (dragToc) {
          const sharePages = shareTocIds.filter((uuid) => uuid !== dragToc);
          const index = sharePages.findIndex((uuid) => uuid === dropId);
          if (position === 'top') {
            sharePages.splice(index, 0, dragToc);
          } else if (position === 'bottom') {
            sharePages.splice(index + 1, 0, dragToc);
          }
          updateSpaceView($currentUserCache.currentSpaceViewId, { sharePages });
        }
      } else if (recordSort && viewId) {
        const sourceBlock = selectedBlocks.map((item) => ({
          uuid: item.blockId,
          oldGroupValues: item.groupValues,
        }));

        if (position === 'top') {
          sortRecord(viewId, sourceBlock, { before: dropId, ...collectionGroupInfo });
        }
        if (position === 'bottom') {
          sortRecord(viewId, sourceBlock, { after: dropId, ...collectionGroupInfo });
        }

        const newSelectedBlocks = selectedBlocks.map((item) => {
          return {
            ...item,
            groupValues: groupValue ? { [groupValue]: subGroupValue ? [subGroupValue] : [] } : {},
          };
        });
        dispatch(uiActions.update({ selectedBlocks: newSelectedBlocks, selectedCells: [] }));
      } else {
        let newBlocks: SelectBlock[] | undefined = undefined;
        if (copy) {
          newBlocks = await copyBlock(idsToSelectedBlocks(ids), dropId);
        }
        if (position === 'top') {
          if (newBlocks) {
            const newIds = selectedBlocksToIds(newBlocks);
            moveBlock(
              newIds,
              { parentId: dropBlock.parentId, before: dropId, ...collectionGroupInfo },
              viewId
            );
            selectedBlockHistory(newBlocks);
            return;
          }
          moveBlock(
            ids,
            { parentId: dropBlock.parentId, before: dropId, ...collectionGroupInfo },
            viewId
          );
        } else if (position === 'middle') {
          const dropBlock = getRefBlock(dropId);
          if (newBlocks) {
            const newIds = selectedBlocksToIds(newBlocks);
            moveBlock(
              newIds,
              { parentId: dropBlock?.uuid ?? dropId, last: true, ...collectionGroupInfo },
              viewId
            );
            selectedBlockHistory(newBlocks);
            return;
          }
          moveBlock(
            ids,
            { parentId: dropBlock?.uuid ?? dropId, last: true, ...collectionGroupInfo },
            viewId
          );
        } else if (position === 'bottom') {
          if (dropInChild) {
            if (newBlocks) {
              const newIds = selectedBlocksToIds(newBlocks);
              moveBlock(newIds, { parentId: dropId, first: true, ...collectionGroupInfo }, viewId);
              selectedBlockHistory(newBlocks);
              return;
            }
            moveBlock(ids, { parentId: dropId, first: true, ...collectionGroupInfo }, viewId);
          } else if (
            cursorArea === Aside.BOTTOM &&
            dragToc === dropId &&
            dropBlock.parentId !== dropBlock.spaceId
          ) {
            if (!dropParentBlock) return;
            // 将目录最下面的 拖拽到目录最下面时 需特殊处理，移动到父的父的最后一个
            if (newBlocks) {
              const newIds = selectedBlocksToIds(newBlocks);
              moveBlock(newIds, { parentId: dropId, first: true, ...collectionGroupInfo }, viewId);
              selectedBlockHistory(newBlocks);
              return;
            }
            moveBlock(
              ids,
              { parentId: dropParentBlock.parentId, last: true, ...collectionGroupInfo },
              viewId
            );
          } else {
            if (newBlocks) {
              const newIds = selectedBlocksToIds(newBlocks);
              moveBlock(
                newIds,
                { parentId: dropBlock.parentId, after: dropId, ...collectionGroupInfo },
                viewId
              );
              selectedBlockHistory(newBlocks);
              return;
            }
            moveBlock(
              ids,
              { parentId: dropBlock.parentId, after: dropId, ...collectionGroupInfo },
              viewId
            );
          }
        } else {
          if (newBlocks) {
            const newIds = selectedBlocksToIds(newBlocks);
            addColumn(newIds, dropId, position);
            selectedBlockHistory(newBlocks);
            return;
          }
          addColumn(ids, dropId, position);
          // 积分任务
          void updateTask(ActivityIds.CREATE_COLUMNS_BLOCK, ActivitiesListType.basicList);
          void updateTask(
            ActivityIds.GUIDE_FIRST_TIME_CREATED_COLUMN_LIST,
            ActivitiesListType.basicList
          );
        }
      }

      if (!recordSort) {
        selectedBlockHistory(idsToSelectedBlocks(ids, viewId, syncId));
      }
    });
  };

  return useCallback(onDrop, [
    transaction,
    confirmDisableSortersUI,
    updateSpaceView,
    copyBlock,
    selectedBlockHistory,
    updateTask,
  ]);
};
